import React, { memo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FunnelIcon, XMarkIcon, AdjustmentsHorizontalIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';

export interface JobFilters {
  sortBy: 'recent' | 'relevant';
  datePosted?: '1' | '7' | '30' | 'all';
  experienceLevel?: 'internship' | 'entry' | 'associate' | 'mid-senior' | 'director';
  jobType?: 'full_time' | 'part_time' | 'contract' | 'temporary' | 'internship';
  remote?: boolean;
  location?: string;
  company?: string;
  title?: string;
}

interface JobFiltersProps {
  filters: JobFilters;
  onFilterChange: (filters: JobFilters) => void;
  onReset: () => void;
}

const JobFiltersComponent: React.FC<JobFiltersProps> = ({ filters, onFilterChange, onReset }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState<JobFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleChange = (key: keyof JobFilters, value: any) => {
    const newFilters = {
      ...localFilters,
      [key]: value === '' ? undefined : value
    };
    setLocalFilters(newFilters);
  };

  const handleSearch = () => {
    onFilterChange(localFilters);
  };

  const handleReset = () => {
    const resetFilters: JobFilters = {
      company: '',
      location: '',
      title: '',
      sortBy: 'recent',
      datePosted: undefined,
      experienceLevel: undefined,
      jobType: undefined,
      remote: false
    };
    setLocalFilters(resetFilters);
    onReset();
  };

  const filterCount = Object.values(filters).filter(value => value !== undefined && value !== '').length;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-xl p-8 mb-8 border border-gray-100/50"
    >
      <div className="flex items-center justify-between">
        <motion.button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-3 group"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2.5 rounded-xl group-hover:shadow-lg transition-all duration-200">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-white" />
          </div>
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">Filters</h2>
            {filterCount > 0 && (
              <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
                {filterCount}
              </span>
            )}
          </div>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleReset}
          className="text-sm text-gray-500 hover:text-blue-600 flex items-center px-4 py-2 rounded-xl hover:bg-blue-50/50 transition-all duration-200"
        >
          <XMarkIcon className="h-4 w-4 mr-2" />
          Clear All
        </motion.button>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-8">
              {/* Job Title */}
              <div className="space-y-2.5 group">
                <label className="block text-sm font-medium text-gray-600 group-hover:text-blue-600 transition-colors duration-200">
                  Job Title
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={localFilters.title || ''}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Search by title..."
                    className="w-full rounded-xl border-2 border-gray-200 bg-white/50 pl-10 pr-4 py-2.5 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 hover:border-blue-400 transition-all duration-200 placeholder:text-gray-400"
                  />
                </div>
              </div>

              {/* Sort By */}
              <div className="space-y-2.5 group">
                <label className="block text-sm font-medium text-gray-600 group-hover:text-blue-600 transition-colors duration-200">
                  Sort By
                </label>
                <div className="relative">
                  <select
                    value={localFilters.sortBy}
                    onChange={(e) => handleChange('sortBy', e.target.value as 'recent' | 'relevant')}
                    className="w-full rounded-xl border-2 border-gray-200 bg-white/50 px-4 py-2.5 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 hover:border-blue-400 transition-all duration-200 appearance-none cursor-pointer"
                  >
                    <option value="recent">Most Recent</option>
                    <option value="relevant">Most Relevant</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Date Posted */}
              <div className="space-y-2.5 group">
                <label className="block text-sm font-medium text-gray-600 group-hover:text-blue-600 transition-colors duration-200">
                  Date Posted
                </label>
                <div className="relative">
                  <select
                    value={localFilters.datePosted || 'all'}
                    onChange={(e) => handleChange('datePosted', e.target.value)}
                    className="w-full rounded-xl border-2 border-gray-200 bg-white/50 px-4 py-2.5 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 hover:border-blue-400 transition-all duration-200 appearance-none cursor-pointer"
                  >
                    <option value="all">Any Time</option>
                    <option value="1">Last 24 Hours</option>
                    <option value="7">Past Week</option>
                    <option value="30">Past Month</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Experience Level */}
              <div className="space-y-2.5 group">
                <label className="block text-sm font-medium text-gray-600 group-hover:text-blue-600 transition-colors duration-200">
                  Experience Level
                </label>
                <div className="relative">
                  <select
                    value={localFilters.experienceLevel || ''}
                    onChange={(e) => handleChange('experienceLevel', e.target.value)}
                    className="w-full rounded-xl border-2 border-gray-200 bg-white/50 px-4 py-2.5 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 hover:border-blue-400 transition-all duration-200 appearance-none cursor-pointer"
                  >
                    <option value="">All Levels</option>
                    <option value="internship">Internship</option>
                    <option value="entry">Entry Level</option>
                    <option value="associate">Associate</option>
                    <option value="mid-senior">Mid-Senior Level</option>
                    <option value="director">Director</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Job Type */}
              <div className="space-y-2.5 group">
                <label className="block text-sm font-medium text-gray-600 group-hover:text-blue-600 transition-colors duration-200">
                  Job Type
                </label>
                <div className="relative">
                  <select
                    value={localFilters.jobType || ''}
                    onChange={(e) => handleChange('jobType', e.target.value)}
                    className="w-full rounded-xl border-2 border-gray-200 bg-white/50 px-4 py-2.5 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 hover:border-blue-400 transition-all duration-200 appearance-none cursor-pointer"
                  >
                    <option value="">All Types</option>
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="space-y-2.5 group">
                <label className="block text-sm font-medium text-gray-600 group-hover:text-blue-600 transition-colors duration-200">
                  Location
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={localFilters.location || ''}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="Enter location..."
                    className="w-full rounded-xl border-2 border-gray-200 bg-white/50 pl-10 pr-4 py-2.5 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 hover:border-blue-400 transition-all duration-200 placeholder:text-gray-400"
                  />
                </div>
              </div>

              {/* Company */}
              <div className="space-y-2.5 group">
                <label className="block text-sm font-medium text-gray-600 group-hover:text-blue-600 transition-colors duration-200">
                  Company
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={localFilters.company || ''}
                    onChange={(e) => handleChange('company', e.target.value)}
                    placeholder="Enter company name..."
                    className="w-full rounded-xl border-2 border-gray-200 bg-white/50 pl-10 pr-4 py-2.5 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 hover:border-blue-400 transition-all duration-200 placeholder:text-gray-400"
                  />
                </div>
              </div>

              {/* Remote Toggle */}
              <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:shadow-md transition-all duration-200">
                <label className="text-sm font-medium text-gray-700">Remote Only</label>
                <Switch
                  checked={localFilters.remote || false}
                  onChange={(checked) => handleChange('remote', checked)}
                  className={`${
                    localFilters.remote ? 'bg-gradient-to-r from-blue-500 to-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ease-spring focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      localFilters.remote ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-all duration-300 ease-spring`}
                  />
                </Switch>
              </div>
            </div>

            {/* Search Button */}
            <div className="mt-8 flex justify-center">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSearch}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
                <span>Search Jobs</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const JobFilters = memo(JobFiltersComponent, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.filters) === JSON.stringify(nextProps.filters);
});

export default JobFilters;